import { useRef, useState } from 'react'
import { PanelError, isOutcomeUnknown } from '@/console/api/errors'
import { McisCutoverRollbackResendRequest, McisCutoverRollbackResendResponse } from '@/console/api/types'
import { requestMcisCutoverRollbackResend } from '@/console/data/mcisCutoverRollbackResend'
import {
  ExecutionBlocker,
  ParsedOutboundResultErpIds,
  ReasonProblem,
  ResendInput,
  ResendJudgement,
  conflictResponseOf,
  executionBlockersOf,
  interpretExecuteError,
  interpretJudgeError,
  parseOutboundResultErpIds,
  reasonProblemOf,
  toRequest,
  toResendInput,
} from './mcisCutoverRollbackResend'

export const RESEND_REQUEST_KIND = { JUDGING: 'judging', EXECUTING: 'executing' } as const
export type ResendRequestKind = (typeof RESEND_REQUEST_KIND)[keyof typeof RESEND_REQUEST_KIND]

/** 실행 한 번의 결과. 실패해도 남겨 재판정 근거로 쓴다. 409 는 실패가 아니라 `response` 로 온다. */
export type ResendExecution = {
  input: ResendInput
  response: McisCutoverRollbackResendResponse | null
  error: PanelError | null
  /** 응답 없이 끝나 서버가 실행했는지 알 수 없다. */
  outcomeUnknown: boolean
}

export type McisCutoverRollbackResendFlow = {
  idsText: string
  setIdsText: (idsText: string) => void
  reasonText: string
  setReasonText: (reasonText: string) => void
  parsed: ParsedOutboundResultErpIds
  reasonProblem: ReasonProblem | null
  /** 지금 입력이 판정할 수 있는 상태면 그 값, 아니면 null. */
  currentInput: ResendInput | null
  inFlight: ResendRequestKind | null
  judgeError: PanelError | null
  judgement: ResendJudgement | null
  /** 판정이 없으면 null. 빈 배열이면 실행할 수 있다. */
  executionBlockers: ExecutionBlocker[] | null
  canExecute: boolean
  execution: ResendExecution | null
  judge: () => void
  execute: () => void
}

type SendResult = { response: McisCutoverRollbackResendResponse } | { response: null; cause: unknown }

/**
 * 호출 하나를 보내고 진행 상태를 관리한다. 실패를 결과로 접어 돌려주고 재시도하지 않는다.
 * `sending` 은 리렌더 전에 들어온 두 번째 호출까지 막는다 — 그 호출은 보내지 않고 null 로 끝난다.
 */
const useResendRequest = () => {
  const [inFlight, setInFlight] = useState<ResendRequestKind | null>(null)
  const sending = useRef(false)

  const send = async (request: McisCutoverRollbackResendRequest, kind: ResendRequestKind): Promise<SendResult | null> => {
    if (sending.current) return null
    sending.current = true
    setInFlight(kind)
    try {
      return { response: await requestMcisCutoverRollbackResend(request) }
    } catch (cause) {
      return { response: null, cause }
    } finally {
      sending.current = false
      setInFlight(null)
    }
  }

  return { inFlight, send }
}

const toExecution = (input: ResendInput, result: SendResult): ResendExecution => {
  if (result.response) return { input, response: result.response, error: null, outcomeUnknown: false }
  const conflict = conflictResponseOf(result.cause)
  if (conflict) return { input, response: conflict, error: null, outcomeUnknown: false }
  return { input, response: null, error: interpretExecuteError(result.cause), outcomeUnknown: isOutcomeUnknown(result.cause) }
}

/** `send` 가 실패를 결과로 접어 돌려주므로 거절될 일이 없어, 화면 이벤트에서는 기다리지 않고 시작만 한다. */
const fireAndForget = (task: () => Promise<void>) => (): void => {
  task().catch(() => undefined)
}

/**
 * 입력 → 판정(dryRun) → 확인 → 실행. 실행은 판정에 쓴 id 집합·사유를 그대로 dryRun=false 로 보낸다.
 * 실행이 끝나면 결과와 상관없이 판정을 지운다 — 서버 상태가 바뀌었거나 바뀌었는지 알 수 없어 다시 판정해야 한다.
 */
export const useMcisCutoverRollbackResendFlow = (): McisCutoverRollbackResendFlow => {
  const [idsText, setIdsText] = useState('')
  const [reasonText, setReasonText] = useState('')
  const [judgement, setJudgement] = useState<ResendJudgement | null>(null)
  const [judgeError, setJudgeError] = useState<PanelError | null>(null)
  const [execution, setExecution] = useState<ResendExecution | null>(null)
  const { inFlight, send } = useResendRequest()

  const parsed = parseOutboundResultErpIds(idsText)
  const reasonProblem = reasonProblemOf(reasonText)
  const currentInput = toResendInput(parsed, reasonText)
  const executionBlockers = executionBlockersOf(judgement, currentInput, inFlight !== null)
  const canExecute = executionBlockers !== null && executionBlockers.length === 0

  const judge = async (): Promise<void> => {
    if (!currentInput || inFlight) return
    const input = currentInput
    setJudgement(null)
    setJudgeError(null)
    const result = await send(toRequest(input, true), RESEND_REQUEST_KIND.JUDGING)
    if (!result) return
    if (result.response) {
      setJudgement({ input, response: result.response })
      return
    }
    setJudgeError(interpretJudgeError(result.cause))
  }

  // 확인 다이얼로그가 열린 사이에도 입력·진행 상태가 바뀔 수 있어 누른 시점에 다시 확인한다.
  const execute = async (): Promise<void> => {
    if (!canExecute || !judgement) return
    const { input } = judgement
    const result = await send(toRequest(input, false), RESEND_REQUEST_KIND.EXECUTING)
    if (!result) return
    setJudgement(null)
    setExecution(toExecution(input, result))
  }

  return {
    idsText,
    setIdsText,
    reasonText,
    setReasonText,
    parsed,
    reasonProblem,
    currentInput,
    inFlight,
    judgeError,
    judgement,
    executionBlockers,
    canExecute,
    execution,
    judge: fireAndForget(judge),
    execute: fireAndForget(execute),
  }
}
